import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { LegalSection, LegalDisclaimer, LegalLastUpdated } from '../LegalLayout';
import { APP_NAME } from '../../config/branding';
import { LEGAL_ENTITY, MIN_AGE_YEARS } from '../legalConstants';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms & Conditions"
      subtitle={`User agreement for accessing and using ${APP_NAME}.`}
    >
      <LegalLastUpdated />

      <LegalSection title="Agreement">
        <p>
          These Terms & Conditions (&quot;Terms&quot;) form a binding agreement between you and {LEGAL_ENTITY} regarding
          your use of {APP_NAME}. By creating an account, signing in, or using the service, you confirm that you have
          read, understood, and agree to these Terms, our{' '}
          <Link to="/privacy" className="legal-inline-link">Privacy Policy</Link>,{' '}
          <Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link>, and related policies
          linked herein.
        </p>
      </LegalSection>

      <LegalSection title="Eligibility">
        <ul>
          <li>You must be at least {MIN_AGE_YEARS} years old.</li>
          <li>You must provide accurate registration information and keep it up to date.</li>
          <li>You must not be barred from using the service under applicable law.</li>
          <li>One person may not maintain multiple accounts for abuse, evasion, or impersonation.</li>
        </ul>
      </LegalSection>

      <LegalSection title="Your account">
        <p>
          You are responsible for safeguarding your credentials and for all activity under your account. Notify us
          immediately at {' '}
          {/* <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a>  */}
          {/* or use the{' '} */}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
          if you
          suspect unauthorized access. We may suspend or terminate accounts that violate these Terms or pose a security
          risk.
        </p>
      </LegalSection>

      <LegalSection title="Acceptable use">
        <p>
          You agree to use {APP_NAME} lawfully and in accordance with our{' '}
          <Link to="/legal/acceptable-use" className="legal-inline-link">Acceptable Use & Misuse</Link> policy and{' '}
          <Link to="/legal/code-of-conduct" className="legal-inline-link">Code of Conduct</Link>. Prohibited conduct
          includes harassment, hate speech, illegal content, spam, malware distribution, unauthorized scraping, and
          attempts to circumvent security controls.
        </p>
      </LegalSection>

      <LegalSection title="Your content">
        <p>
          You retain ownership of content you submit. By posting on {APP_NAME}, you grant {LEGAL_ENTITY} a non-exclusive,
          worldwide, royalty-free license to host, store, reproduce, display, and distribute your content solely for
          operating, promoting, and improving the service — including moderation and backup. You represent that you have
          the rights to share such content and that it does not infringe third-party rights.
        </p>
        <p>
          See our <Link to="/legal/fair-use" className="legal-inline-link">Fair Use Policy</Link> for guidance on
          referencing and sharing others&apos; materials.
        </p>
      </LegalSection>

      <LegalSection title="Personal and social profiles">
        <p>
          {APP_NAME} offers separate personal and optional social profile identities. You are responsible for content
          posted under each identity. Switching profiles changes how you appear to others; verify your active mode before
          posting. See <Link to="/docs" className="legal-inline-link">Documentation</Link> for details.
        </p>
      </LegalSection>

      <LegalSection title="Moderation and enforcement">
        <p>
          We may review, remove, restrict, or disable access to content or accounts that violate these Terms or harm the
          community — with or without notice. We are not obligated to monitor all content but may do so. Report concerns
          via our <Link to="/legal/safety" className="legal-inline-link">Safety & Reporting</Link> page.
        </p>
      </LegalSection>

      <LegalSection title="Disclaimer of warranties">
        <p>
          {APP_NAME} is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, whether
          express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do
          not warrant uninterrupted, error-free, or secure operation.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, {LEGAL_ENTITY} and its officers, directors, employees, and agents shall
          not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits,
          data, goodwill, or other intangible losses arising from your use of {APP_NAME}. Where direct liability cannot be
          excluded, any compensation, refund, or similar remedy is not automatic; we will consider claims individually
          and address them at our discretion, consistent with applicable law.
        </p>
        <p>
          We are not responsible for user-generated content, third-party links, or actions of other users. See{' '}
          <Link to="/legal/acceptable-use" className="legal-inline-link">Acceptable Use & Misuse</Link> for our approach
          to reported violations.
        </p>
      </LegalSection>

      <LegalSection title="Indemnification">
        <p>
          You agree to indemnify and hold harmless {LEGAL_ENTITY} from claims, damages, losses, and expenses (including
          reasonable legal fees) arising from your use of the service, your content, or your violation of these Terms.
        </p>
      </LegalSection>

      <LegalSection title="Termination">
        <p>
          You may stop using {APP_NAME} at any time. We may suspend or terminate your access for any reason, including
          violations of these Terms. Provisions that by nature should survive termination (including liability limits,
          indemnification, and dispute terms) will remain in effect.
        </p>
      </LegalSection>

      <LegalSection title="Governing law and disputes">
        <p>
          These Terms are governed by applicable laws of the jurisdiction in which {LEGAL_ENTITY} operates, without regard
          to conflict-of-law principles. Disputes shall be resolved in competent courts of that jurisdiction unless
          mandatory consumer protection laws in your country require otherwise. See{' '}
          <Link to="/legal/regional" className="legal-inline-link">Regional Compliance</Link> for location-specific notes.
        </p>
      </LegalSection>

      <LegalSection title="Changes">
        <p>
          We may modify these Terms at any time without prior notice. Updated Terms take effect when published. Continued
          use after changes constitutes acceptance. If you do not agree, discontinue use of {APP_NAME}.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          Legal and terms inquiries: {' '}
          {/* <a href={`mailto:${SUPPORT_EMAIL}`} className="legal-inline-link">{SUPPORT_EMAIL}</a>. */}
          <Link to="/contact" className="legal-inline-link">Contact</Link> form.
        </p>
      </LegalSection>

      <LegalDisclaimer />
    </LegalLayout>
  );
}
